import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { chiusureApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus, 
  ChevronRight, 
  CalendarDays,
  Loader2,
  Search,
  Filter,
  X,
  ArrowUpDown,
  Clock
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { it } from 'date-fns/locale';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const ChiusureListPage = () => {
  const navigate = useNavigate();
  const [chiusure, setChiusure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dataInizio, setDataInizio] = useState(null);
  const [dataFine, setDataFine] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [sortOrder, setSortOrder] = useState('desc');
  const [calendarOpenInizio, setCalendarOpenInizio] = useState(false);
  const [calendarOpenFine, setCalendarOpenFine] = useState(false);

  useEffect(() => {
    loadChiusure();
  }, [dataInizio, dataFine]);

  const loadChiusure = async () => {
    setLoading(true);
    try {
      const data = await chiusureApi.getAll(
        0, 
        1000,
        dataInizio ? format(dataInizio, 'yyyy-MM-dd') : null,
        dataFine ? format(dataFine, 'yyyy-MM-dd') : null
      );
      setChiusure(data);
    } catch (error) {
      toast.error('Errore nel caricamento delle chiusure');
    } finally {
      setLoading(false);
    }
  };

  const getDayBadgeClass = (giorno) => {
    const classes = {
      'LUN': 'day-badge-lun',
      'MAR': 'day-badge-mar',
      'MER': 'day-badge-mer',
      'GIO': 'day-badge-gio',
      'VEN': 'day-badge-ven',
      'SAB': 'day-badge-sab',
      'DOM': 'day-badge-dom',
    };
    return classes[giorno] || 'bg-muted text-muted-foreground';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'd MMMM yyyy', { locale: it });
    } catch {
      return dateStr;
    }
  };

  const clearFilters = () => {
    setDataInizio(null);
    setDataFine(null);
    setSearchTerm('');
  };

  const setQuickFilter = (type) => {
    const today = new Date();
    switch (type) {
      case 'this-month':
        setDataInizio(startOfMonth(today));
        setDataFine(endOfMonth(today));
        break;
      case 'last-month':
        const lastMonth = subMonths(today, 1);
        setDataInizio(startOfMonth(lastMonth));
        setDataFine(endOfMonth(lastMonth));
        break;
      case 'last-3-months':
        setDataInizio(startOfMonth(subMonths(today, 2)));
        setDataFine(endOfMonth(today));
        break;
      default:
        clearFilters();
    }
  };

  const filteredChiusure = chiusure.filter(c => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      c.data_servizio.includes(searchTerm) ||
      c.giorno_settimana.toLowerCase().includes(searchLower) ||
      formatDate(c.data_servizio).toLowerCase().includes(searchLower)
    );
  });

  // Sort chiusure
  const sortedChiusure = [...filteredChiusure].sort((a, b) => {
    const dateA = new Date(a.data_servizio);
    const dateB = new Date(b.data_servizio);
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // Group chiusure by month
  const groupedChiusure = sortedChiusure.reduce((acc, chiusura) => {
    const monthKey = format(new Date(chiusura.data_servizio), 'MMMM yyyy', { locale: it });
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(chiusura);
    return acc;
  }, {});

  const hasActiveFilters = dataInizio || dataFine || searchTerm;

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/')}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold font-heading tracking-tight">Storico Chiusure</h1>
            </div>
            <Button
              variant={showFilters ? "default" : "outline"}
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="filter-toggle-btn"
              className={hasActiveFilters && !showFilters ? "border-primary text-primary" : ""}
            >
              <Filter className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              data-testid="search-input"
              type="text"
              placeholder="Cerca per data..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12"
            />
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-secondary/50 rounded-xl space-y-4 animate-slide-up">
              {/* Quick Filters */}
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setQuickFilter('this-month')}
                  data-testid="filter-this-month"
                >
                  Questo mese
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setQuickFilter('last-month')}
                  data-testid="filter-last-month"
                >
                  Mese scorso
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setQuickFilter('last-3-months')}
                  data-testid="filter-last-3-months"
                >
                  Ultimi 3 mesi
                </Button>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Da</p>
                  <Popover open={calendarOpenInizio} onOpenChange={setCalendarOpenInizio}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left font-normal h-10"
                        data-testid="date-from-btn"
                      >
                        <CalendarDays className="w-4 h-4 mr-2" />
                        {dataInizio ? format(dataInizio, 'd MMM', { locale: it }) : 'Seleziona'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dataInizio}
                        onSelect={(date) => {
                          setDataInizio(date);
                          setCalendarOpenInizio(false);
                        }}
                        locale={it}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">A</p>
                  <Popover open={calendarOpenFine} onOpenChange={setCalendarOpenFine}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left font-normal h-10"
                        data-testid="date-to-btn"
                      >
                        <CalendarDays className="w-4 h-4 mr-2" />
                        {dataFine ? format(dataFine, 'd MMM', { locale: it }) : 'Seleziona'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dataFine}
                        onSelect={(date) => {
                          setDataFine(date);
                          setCalendarOpenFine(false);
                        }}
                        locale={it}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Sort & Clear */}
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  data-testid="sort-btn"
                >
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  {sortOrder === 'desc' ? 'Più recenti' : 'Più vecchie'}
                </Button>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-destructive"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Rimuovi filtri
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-lg mx-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : sortedChiusure.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <CalendarDays className="w-16 h-16 text-muted-foreground/50 mb-4" />
              <h3 className="font-semibold text-lg mb-2">Nessuna chiusura trovata</h3>
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters ? 'Prova a modificare i filtri' : 'Inizia registrando la prima chiusura'}
              </p>
              {hasActiveFilters ? (
                <Button variant="outline" onClick={clearFilters}>
                  Rimuovi filtri
                </Button>
              ) : (
                <Button onClick={() => navigate('/nuova-chiusura')}>
                  <Plus className="w-5 h-5 mr-2" />
                  Nuova Chiusura
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Summary */}
            {hasActiveFilters && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground mb-2">Periodo selezionato</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-2xl font-bold font-mono-numbers">
                        {sortedChiusure.reduce((sum, c) => sum + c.coperti_totali_giorno, 0)}
                      </p>
                      <p className="text-xs text-muted-foreground">Coperti totali</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold font-mono-numbers text-green-600">
                        {formatCurrency(sortedChiusure.reduce((sum, c) => sum + c.incasso_giorno, 0))}
                      </p>
                      <p className="text-xs text-muted-foreground">Incasso totale</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {Object.entries(groupedChiusure).map(([month, items]) => (
              <section key={month}>
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 capitalize">
                  {month}
                </h2>
                <div className="space-y-2">
                  {items.map((chiusura, index) => (
                    <Card 
                      key={chiusura.id}
                      data-testid={`chiusura-list-item-${chiusura.id}`}
                      className={`receipt-stub cursor-pointer card-lift animate-slide-up ${chiusura.stato === 'parziale' ? 'border-amber-300 bg-amber-50/50' : ''}`}
                      style={{ animationDelay: `${index * 30}ms` }}
                      onClick={() => chiusura.stato === 'parziale' ? navigate(`/nuova-chiusura?completa=${chiusura.id}`) : navigate(`/chiusure/${chiusura.id}`)}
                    >
                      <CardContent className="flex items-center justify-between p-4 pl-6">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className={`day-badge ${getDayBadgeClass(chiusura.giorno_settimana)} flex-shrink-0`}>
                            {chiusura.giorno_settimana}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold">
                                {format(new Date(chiusura.data_servizio), 'd MMMM', { locale: it })}
                              </p>
                              {chiusura.stato === 'parziale' && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-200 text-amber-800 rounded-full">
                                  <Clock className="w-3 h-3" />
                                  Da completare
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                              <span className="text-muted-foreground">
                                <span className="font-mono-numbers">{chiusura.coperti_totali_giorno}</span> coperti
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono-numbers font-semibold text-green-600 text-sm" data-testid={`incasso-${chiusura.id}`}>
                            {formatCurrency(chiusura.incasso_giorno)}
                          </span>
                          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      {/* FAB */}
      <Button
        data-testid="fab-new-chiusura"
        onClick={() => navigate('/nuova-chiusura')}
        className="fixed bottom-24 right-4 w-14 h-14 rounded-full shadow-xl z-50"
      >
        <Plus className="w-6 h-6" />
      </Button>
    </div>
  );
};

export default ChiusureListPage;
